import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Store, Package } from "lucide-react";
import api from "../lib/api";
import { Input } from "./ui/input";
import { fmt } from "../lib/money";

/**
 * Marketplace search with predictive autocomplete.
 * - Triggers suggestions at 4+ characters (per product brief).
 * - Debounced 220ms; cancels stale requests.
 * - Suggests matching sellers/shops AND matching products.
 * - Enter submits the current text as a full-page search.
 */
export default function SearchBar() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sellers, setSellers] = useState([]);
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState(-1);   // combined index for keyboard nav
  const wrapRef = useRef(null);
  const timerRef = useRef(null);
  const reqRef = useRef(0);

  // Close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Debounced fetch
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const needle = q.trim();
    if (needle.length < 4) {
      setSellers([]); setProducts([]); setLoading(false); setActive(-1);
      return;
    }
    setLoading(true);
    const myReq = ++reqRef.current;
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search/suggest?q=${encodeURIComponent(needle)}`);
        if (myReq !== reqRef.current) return;    // stale
        setSellers(data.sellers || []);
        setProducts(data.products || []);
        setActive(-1);
      } catch {
        if (myReq === reqRef.current) { setSellers([]); setProducts([]); }
      } finally {
        if (myReq === reqRef.current) setLoading(false);
      }
    }, 220);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [q]);

  const combined = [
    ...sellers.map((s) => ({ kind: "seller", ...s })),
    ...products.map((p) => ({ kind: "product", ...p })),
  ];

  const goTo = (item) => {
    setOpen(false);
    navigate(item.url);
  };

  const submit = (e) => {
    e.preventDefault();
    if (active >= 0 && combined[active]) {
      goTo(combined[active]);
      return;
    }
    if (q.trim()) navigate(`/products?q=${encodeURIComponent(q.trim())}`);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || combined.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, combined.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, -1)); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const showDropdown = open && q.trim().length >= 4;
  const hasResults = combined.length > 0;

  return (
    <div ref={wrapRef} className="flex-1 max-w-2xl relative">
      <form onSubmit={submit}>
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
        <Input
          data-testid="search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          autoComplete="off"
          placeholder="Search products, shops or sellers…"
          className="pl-9 h-10 rounded-none border-black focus-visible:ring-0 focus-visible:border-[#0033FF]"
        />
      </form>

      {showDropdown && (
        <div
          data-testid="search-suggest-dropdown"
          className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-black shadow-[6px_6px_0_0_#000] z-50 max-h-[70vh] overflow-y-auto"
        >
          {loading && (
            <div className="px-4 py-3 text-xs text-neutral-500">Searching…</div>
          )}

          {!loading && !hasResults && (
            <div className="px-4 py-4 text-sm text-neutral-500" data-testid="search-suggest-empty">
              No shops or products match "{q.trim()}".
              <button
                type="button"
                onClick={submit}
                className="ml-2 underline text-[#0033FF] hover:text-[#0022CC]"
              >
                Full search anyway →
              </button>
            </div>
          )}

          {!loading && sellers.length > 0 && (
            <div>
              <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
                Shops &amp; sellers
              </div>
              {sellers.map((s, i) => {
                const idx = i;
                return (
                  <button
                    type="button"
                    key={s.user_id}
                    data-testid={`suggest-seller-${s.user_id}`}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => goTo(s)}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-left border-t border-neutral-100 ${
                      active === idx ? "bg-neutral-100" : "hover:bg-neutral-50"
                    }`}
                  >
                    {s.picture ? (
                      <img src={s.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-neutral-200 grid place-items-center">
                        <Store className="w-4 h-4 text-neutral-500" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{s.shop_name || s.name}</div>
                      {s.shop_name && s.name && (
                        <div className="text-[11px] text-neutral-500 truncate">{s.name}</div>
                      )}
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">Shop</span>
                  </button>
                );
              })}
            </div>
          )}

          {!loading && products.length > 0 && (
            <div>
              <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
                Products
              </div>
              {products.map((p, i) => {
                const idx = sellers.length + i;
                return (
                  <button
                    type="button"
                    key={p.product_id}
                    data-testid={`suggest-product-${p.product_id}`}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => goTo(p)}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-left border-t border-neutral-100 ${
                      active === idx ? "bg-neutral-100" : "hover:bg-neutral-50"
                    }`}
                  >
                    {p.image ? (
                      <img src={p.image} alt="" className="w-10 h-10 object-cover border border-neutral-200" />
                    ) : (
                      <div className="w-10 h-10 bg-neutral-200 grid place-items-center">
                        <Package className="w-4 h-4 text-neutral-500" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{p.title}</div>
                      <div className="text-[11px] text-neutral-500 truncate">{p.seller_name}</div>
                    </div>
                    <div className="text-sm font-bold whitespace-nowrap">{fmt(p.price)}</div>
                  </button>
                );
              })}
            </div>
          )}

          {!loading && hasResults && (
            <button
              type="button"
              data-testid="suggest-full-search"
              onClick={submit}
              className="w-full text-left border-t border-neutral-200 px-4 py-2 text-xs uppercase tracking-widest font-bold text-[#0033FF] hover:bg-neutral-50"
            >
              See all results for "{q.trim()}" →
            </button>
          )}
        </div>
      )}
    </div>
  );
}
